import { skipToken } from '@reduxjs/toolkit/query';
import { CheckboxListFormik } from '@strata/react-checkbox-list';
import { FormContainer } from '@strata/react-form-container';
import { FormInputContainer } from '@strata/react-form-input-container';
import { Formik } from 'formik';
import { useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import * as Yup from 'yup';

import { postPreBid, putPreBid } from 'api/auctionsAPI';
import { postFirstRefusalClaim, putFirstRefusalClaim } from 'api/clientAPI';
import { UnsavedChangesDialog } from 'components/admin/automations/dialogs/UnsavedChangesDialog';
import { NewTemplateNote } from 'components/admin/automations/NewTemplateNote';
import { AutomationTemplateProps } from 'components/admin/automations/templates/AutomationTemplatePropsInterface';
import { FrozenPriceNote } from 'components/admin/automations/templates/FrozenPriceNote';
import { TemplateHeaderActions } from 'components/admin/automations/templates/TemplateHeaderActions';
import { ActionSection } from 'components/admin/automations/templateSections/ActionSection';
import { CriteriaSection } from 'components/admin/automations/templateSections/CriteriaSection';
import { DuplicateRuleMessage } from 'components/admin/automations/templateSections/DuplicateRuleMessage';
import { NameSection } from 'components/admin/automations/templateSections/NameSection';
import {
    AutomationRuleActions,
    AutomationRuleCriteria,
    AutomationRuleFormContext,
    findAutomationRuleCriterionIndex,
    findAutomationRuleCriterionValue,
    mapProductsToBrandAndGenericProducts,
} from 'services/AutomationRuleHelpers';
import { handleError } from 'services/Error';
import { useAppSelector } from 'store';
import { useGetCanClaimFirstRefusalQuery } from 'store/api/clientApi';
import {
    PayerSegment,
    useGetConfiguredProductsForPayerQuery,
    useGetPayerSegmentsForSupplierQuery,
} from 'store/api/referenceApi';

const formatRuleName = (
    criteria: AutomationRuleFormContext['criteria'],
    action: AutomationRuleFormContext['action'],
) => {
    let ruleName = [
        AutomationRuleCriteria.PAYER,
        AutomationRuleCriteria.PRODUCT,
        AutomationRuleCriteria.PATIENT,
    ]
        .map((key) => findAutomationRuleCriterionValue(criteria, key)?.name)
        .filter((val) => val)
        .join(' & ');

    if (
        action?.type === AutomationRuleActions.SET_MAX_BID &&
        action.value?.some(
            ({ bidAmt, referentPriceType }) => typeof bidAmt === 'number' && referentPriceType,
        )
    ) {
        ruleName += ` = ${action.value
            .map(
                ({ bidAmt, referentPriceType }) =>
                    `${referentPriceType} ${bidAmt < 0 ? '-' : '+'} ${Math.abs(bidAmt)}`,
            )
            .join(' & ')}`;
    } else if (action?.type === AutomationRuleActions.SEND_REFERRAL) {
        ruleName += ` = ${action.type}`;
    }

    return ruleName;
};

export const PatientTemplate = ({
    rule = undefined,
    rules = undefined,
    userCanEdit = false,
    onChange,
    onChangeRule,
    onSubmitSuccess,
    onDeleteSuccess,
    onDuplicate,
}: AutomationTemplateProps) => {
    const selectedNpi = useAppSelector((state) => state.auth.selectedNpi);
    const isExistingRule = !!rule?.id;
    const [showUnsavedChangedDialog, setShowUnsavedChangedDialog] = useState(false);

    const { data: segmentList, isLoading } = useGetPayerSegmentsForSupplierQuery({
        supplierNpi: selectedNpi!,
    });

    const [selectedSegment, setSelectedSegment] = useState<PayerSegment>();

    const { data: configuredProductList } = useGetConfiguredProductsForPayerQuery(
        selectedSegment ? { segmentId: selectedSegment.id } : skipToken,
    );
    const uniqueMedicationList = useMemo(
        () => mapProductsToBrandAndGenericProducts(configuredProductList),
        [configuredProductList],
    );

    const { data: canClaimFirstRefusal } = useGetCanClaimFirstRefusalQuery(
        selectedSegment
            ? { payerId: selectedSegment.payerId, segmentId: selectedSegment.id }
            : skipToken,
    );
    const actionOptions = [
        ...(canClaimFirstRefusal ? [AutomationRuleActions.SEND_REFERRAL] : []),
        AutomationRuleActions.SET_MAX_BID,
    ];

    const initialValues = useMemo(
        () => ({
            criteria: [
                {
                    criterion: AutomationRuleCriteria.PAYER,
                    value: segmentList?.find((segment) => segment.id === rule?.segmentId),
                },
                {
                    criterion: AutomationRuleCriteria.PRODUCT,
                    value: uniqueMedicationList?.find(
                        (med) =>
                            med.oid === rule?.productServiceOid &&
                            (med.isBrand === rule?.isBrand ||
                                rule?.action?.type === AutomationRuleActions.SEND_REFERRAL),
                    ),
                },
                {
                    criterion: AutomationRuleCriteria.PATIENT,
                    value: {
                        name: rule?.memberFirstName,
                        id: rule?.memberId,
                        dob: rule?.memberDOB,
                        benefitCheckSuccess: isExistingRule,
                    },
                },
            ],
            ...ActionSection.initialValues(rule, isExistingRule),
            ...NameSection.initialValues(rule),
        }),
        [isExistingRule, rule, segmentList, uniqueMedicationList],
    );

    const submitRule = async (values: AutomationRuleFormContext) => {
        try {
            let id = rule?.id;
            const payload = {
                npi: selectedNpi!,
                id: id!,
                ...CriteriaSection.submitValues(values),
                ...ActionSection.submitValues(values),
                runOnce: true,
                ...NameSection.submitValues(values),
            };
            if (values.action.type === AutomationRuleActions.SET_MAX_BID) {
                if (isExistingRule) {
                    await putPreBid(payload);
                } else {
                    id = (await postPreBid(payload))?.bid_id;
                }
            } else if (values.action.type === AutomationRuleActions.SEND_REFERRAL) {
                if (isExistingRule) {
                    await putFirstRefusalClaim(payload);
                } else {
                    id = (await postFirstRefusalClaim(payload))?.claim_id;
                }
            }
            await onSubmitSuccess(id?.toLowerCase());
        } catch (err) {
            handleError('An error occurred saving rule. Please try again.', err as Error);
        }
    };

    const duplicateRule = (values: AutomationRuleFormContext) =>
        onDuplicate({
            template: rule?.template,
            ...CriteriaSection.duplicateValues(values),
            ...ActionSection.duplicateValues(values),
            ...NameSection.duplicateValues(values),
        });

    return (
        <Formik
            onSubmit={submitRule}
            initialValues={initialValues as AutomationRuleFormContext}
            enableReinitialize
            validationSchema={Yup.object({
                ...CriteriaSection.validationSchema,
                ...ActionSection.validationSchema,
                ...NameSection.validationSchema,
            })}
        >
            {(formik) => {
                onChange(formik.dirty);

                setSelectedSegment(
                    segmentList?.find(
                        ({ id }) =>
                            id ===
                            findAutomationRuleCriterionValue(
                                formik.values.criteria,
                                AutomationRuleCriteria.PAYER,
                            )?.id,
                    ),
                );

                const payerCriteriaOnChange = async () => {
                    const productIndex = findAutomationRuleCriterionIndex(
                        formik.values.criteria,
                        AutomationRuleCriteria.PRODUCT,
                    );
                    await formik.setFieldValue(`criteria.${productIndex}.value`, undefined);
                    const patientIndex = findAutomationRuleCriterionIndex(
                        formik.values.criteria,
                        AutomationRuleCriteria.PATIENT,
                    );
                    await formik.setFieldValue(
                        `criteria.${patientIndex}.value.benefitCheckSuccess`,
                        false,
                    );
                    await formik.setFieldValue('action', {});
                };

                return (
                    <FormContainer
                        formTitle="Patient Template"
                        onFormSubmit={formik.handleSubmit}
                        onCancelClick={(e) =>
                            formik.dirty ? setShowUnsavedChangedDialog(true) : formik.handleReset(e)
                        }
                        isLoading={isLoading}
                        isSubmitting={!(formik.isValid && formik.dirty) || formik.isSubmitting}
                        formHeaderRightContent={
                            <TemplateHeaderActions
                                rule={rule}
                                userCanEdit={userCanEdit}
                                isExistingRule={isExistingRule}
                                onDeleteSuccess={onDeleteSuccess}
                                duplicateRule={duplicateRule}
                            />
                        }
                        formLeftContent={
                            <div className="sds_automation-template">
                                <UnsavedChangesDialog
                                    isOpen={showUnsavedChangedDialog}
                                    onPrimaryButtonClick={(e) => {
                                        formik.handleReset(e);
                                        setShowUnsavedChangedDialog(false);
                                    }}
                                    onSecondaryButtonClick={() =>
                                        setShowUnsavedChangedDialog(false)
                                    }
                                />
                                <NewTemplateNote />
                                <FrozenPriceNote />
                                <CriteriaSection
                                    userCanEdit={userCanEdit}
                                    criteriaPropsMap={{
                                        [AutomationRuleCriteria.PAYER]: {
                                            values: segmentList,
                                            disabled: isExistingRule,
                                            onChange: payerCriteriaOnChange,
                                        },
                                        [AutomationRuleCriteria.PRODUCT]: {
                                            values: uniqueMedicationList,
                                            onChange: () =>
                                                formik.setFieldValue('action.value', [
                                                    { tempId: uuidv4() },
                                                ]),
                                        },
                                    }}
                                />
                                <DuplicateRuleMessage
                                    rule={rule}
                                    rules={rules}
                                    onChangeRule={onChangeRule}
                                />
                                <ActionSection
                                    actionOptions={actionOptions}
                                    userCanEdit={userCanEdit}
                                    isExistingRule={isExistingRule}
                                />
                                <FormInputContainer>
                                    <div className="sds_automation-template__row">
                                        <div className="sds_automation-template__label sds_label font-bold--uppercase">
                                            Run
                                        </div>
                                        <div>
                                            <CheckboxListFormik
                                                name="frequency"
                                                options={['Once']}
                                                values={['Once']}
                                                disabled
                                                verticalSpacingSmall
                                            />
                                        </div>
                                    </div>
                                </FormInputContainer>
                                <NameSection
                                    userCanEdit={userCanEdit}
                                    formatRuleName={formatRuleName}
                                />
                            </div>
                        }
                    />
                );
            }}
        </Formik>
    );
};
